import { createContext, useContext, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

// Custom hooks and utility functions
import { useThunk } from "../hooks/useThunk";
import { fetchDownloadVersions } from "../store/thunks/trackThunks";

// Create the context
const LoggedUserContext = createContext();

// Custom hook for accessing the context
export const useLoggedUser = () => {
    const context = useContext(LoggedUserContext);
    if (!context) {
        throw new Error(
            "useLoggedUser must be used within a LoggedUserProvider"
        );
    }
    return context;
};

// Provider component that wraps your app and makes user object available to any
// child component that calls useLoggedUser().
export const LoggedUserProvider = ({ children }) => {
    // Thunk to fetch download versions
    const [doFetchDownloadVersions] = useThunk(fetchDownloadVersions);
    const [downloadedVersions, setDownloadedVersions] = useState({});
    const [isBlockedDownload, setIsBlockedDownload] = useState(false);

    const optimisticIncrementDownload = (versionId) => {
        setDownloadedVersions((prev) => {
            const current = typeof prev === 'object' && prev !== null ? prev : {};
            return {
                ...current,
                [versionId]: Math.min((current[versionId] || 0) + 1, 2),
            };
        });
    };

    const optimisticDecrementDownload = (versionId) => {
        setDownloadedVersions((prev) => {
            const current = typeof prev === 'object' && prev !== null ? prev : {};
            return {
                ...current,
                [versionId]: Math.max((current[versionId] || 0) - 1, 0),
            };
        });
    };

    const refreshTimeoutRef = useRef(null);
    const isFetchingRef = useRef(false);
    const pendingRefreshRef = useRef(false);
    const pendingResolvesRef = useRef([]);

    // Check for existing user session on initial load
    useEffect(() => {
        fetchVersionsDownloads()
    }, []);

    // refresh user downloads
    const refreshUserDownloads = async () => {
        // If currently fetching, mark that we need another refresh
        if (isFetchingRef.current) {
            pendingRefreshRef.current = true;
            return new Promise((resolve) => {
                pendingResolvesRef.current.push(resolve);
            });
        }

        // If there is an active timeout, clear it and schedule a new one
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
            return new Promise((resolve) => {
                pendingResolvesRef.current.push(resolve);
                refreshTimeoutRef.current = setTimeout(async () => {
                    refreshTimeoutRef.current = null;
                    await fetchVersionsDownloads();
                }, 500); // Wait 500ms after last call
            });
        }

        // Otherwise, this is the first call. Execute immediately!
        return new Promise(async (resolve) => {
            pendingResolvesRef.current.push(resolve);
            
            // Set a timeout lock to ensure subsequent calls within 500ms are debounced
            refreshTimeoutRef.current = setTimeout(() => {
                refreshTimeoutRef.current = null;
            }, 500);

            await fetchVersionsDownloads();
        });
    };

    const fetchVersionsDownloads = async () => {
        // Prevent concurrent fetches
        if (isFetchingRef.current) {
            pendingRefreshRef.current = true;
            return;
        }

        isFetchingRef.current = true;
        pendingRefreshRef.current = false;

        try {
            // Only fetch if user is authenticated
            const token = localStorage.getItem("token");
            if (!token) {
                return;
            }
            
            console.log("[LoggedUserContext] fetching download versions...");
            const response = await doFetchDownloadVersions();
            console.log("[LoggedUserContext] doFetchDownloadVersions response:", response);
            if (response.success) {
                console.log("[LoggedUserContext] success — versions:", response.response);
                setIsBlockedDownload(false);
                setDownloadedVersions(response.response || {});
            } else {
                console.warn("[LoggedUserContext] fetch failed — error:", response.error, "| isBlocked will NOT change");
                // Only a 429 rate-limit response should block downloads.
                // Any other failure (network glitch, 5xx, etc.) should not
                // permanently lock the user out — just keep the existing state.
                if (response.error?.code === "429") {
                    console.warn("[LoggedUserContext] 429 rate limit — blocking downloads");
                    setIsBlockedDownload(true);
                }
            }
        } catch (error) {
            // useThunk never throws, so this is a safety net only.
            console.error("[LoggedUserContext] unexpected catch in fetchVersionsDownloads:", error);
            // Do not set isBlockedDownload — a transient error should not lock downloads.
            setDownloadedVersions({});
        } finally {
            isFetchingRef.current = false;

            // If another refresh was requested while we were fetching, do it now
            if (pendingRefreshRef.current) {
                pendingRefreshRef.current = false;
                setTimeout(() => fetchVersionsDownloads(), 100);
            } else {
                // Resolve all pending promises
                const resolves = pendingResolvesRef.current;
                pendingResolvesRef.current = [];
                resolves.forEach((resolve) => resolve());
            }
        }
    };

    useEffect(() => {
        return () => {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }
        };
    }, []);

    // Context value with all functions and state
    const contextValue = {
        downloadedVersions,
        isBlockedDownload,
        refreshUserDownloads,
        optimisticIncrementDownload,
        optimisticDecrementDownload,
    };

    return (
        <LoggedUserContext.Provider value={contextValue}>
            {children}
        </LoggedUserContext.Provider>
    );
};

export default LoggedUserContext;